import { Head } from "@inertiajs/react"
import { BarChart, Users, TrendingUp, DollarSign, Calendar, BarChartHorizontal } from "lucide-react"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { dashboardPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: dashboardPath(),
  },
]

interface DashboardProps {
  totalStocks: number
  uniqueClients: number
  uniqueContracts: number
  totalValue: string
  totalValueCents: number
  currency: string
  inpcDateRange: {
    start: string
    end: string
    count: number
  }
  ratesDateRange: {
    start: string
    end: string
    count: number
  }
}

export default function Dashboard({
  totalStocks,
  uniqueClients,
  uniqueContracts,
  totalValue,
  currency,
  inpcDateRange,
  ratesDateRange,
}: DashboardProps) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <div className="flex flex-col gap-5 p-4">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Stocks</CardTitle>
              <BarChart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalStocks}</div>
              <p className="text-xs text-muted-foreground">Total stock entries</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Clients</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{uniqueClients}</div>
              <p className="text-xs text-muted-foreground">Unique clients</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Contracts</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{uniqueContracts}</div>
              <p className="text-xs text-muted-foreground">Unique contracts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Value</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalValue}</div>
              <p className="text-xs text-muted-foreground">Total purchase value ({currency})</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChartHorizontal className="h-5 w-5" />
                INPC Data
              </CardTitle>
              <CardDescription>Available inflation data range</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Date Range:</span>
                  <span className="text-sm">
                    {inpcDateRange.start} - {inpcDateRange.end}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Total Records:</span>
                  <span className="text-sm">{inpcDateRange.count} entries</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Status:</span>
                  <span
                    className={`text-sm ${inpcDateRange.count > 0 ? "text-green-500" : "text-red-500"}`}
                  >
                    {inpcDateRange.count > 0 ? "Available" : "No Data"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Exchange Rates
              </CardTitle>
              <CardDescription>Available exchange rate data range</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Date Range:</span>
                  <span className="text-sm">
                    {ratesDateRange.start} - {ratesDateRange.end}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Total Records:</span>
                  <span className="text-sm">{ratesDateRange.count} entries</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Status:</span>
                  <span
                    className={`text-sm ${ratesDateRange.count > 0 ? "text-green-500" : "text-red-500"}`}
                  >
                    {ratesDateRange.count > 0 ? "Available" : "No Data"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
